import { useState, useEffect } from 'react';
import PartnerKeyForm from './PartnerKeyForm';
import DashboardMain from './DashboardMain';
import { CUSTOMER_API_URL } from '../../../../config';
import {
  clearDashboardStateFromCurrentUrl,
  parseDashboardStateFromSearch
} from '../../utils/dashboardState';

/**
 * Main Dashboard Component
 *
 * This is the main container for the merchant customization dashboard.
 * It manages the two-page workflow:
 * 1. Partner Key Validation (via Refersion API)
 * 2. Dashboard Customization (product selection, filtering, preview, code generation)
 *
 * Shopify variants are preloaded in the background while the user is on the partner key form
 * to minimize loading time when they reach the dashboard.
 */

interface ShopifyVariantInfo {
  shopifyProductId: string;
  shopifyProductTitle: string;
  description: string;
  descriptionHtml: string;
  priceRange: {
    minPrice: string;
    maxPrice: string;
    currencyCode: string;
    compareAtPriceMin?: string;
  };
  productTag: string;
  variants: Array<{
    variantId: string;
    variantTitle: string;
    sku: string;
  }>;
}

export default function Dashboard() {
  const [restoredDashboardState, setRestoredDashboardState] = useState(() =>
    parseDashboardStateFromSearch()
  );
  const [partnerKey, setPartnerKey] = useState<string | null>(
    restoredDashboardState?.partnerKey ?? null
  );
  const [affiliateRfsn, setAffiliateRfsn] = useState<string | undefined>(
    restoredDashboardState?.affiliateRfsn
  );
  const [partnerId, setPartnerId] = useState<string | undefined>(undefined);
  const [shopifyVariants, setShopifyVariants] = useState<
    Map<string, ShopifyVariantInfo>
  >(new Map());
  const [variantsLoading, setVariantsLoading] = useState(true);

  // Fetch Shopify variants and Refersion tracking code once the partner key is known
  useEffect(() => {
    if (!partnerKey) return;

    const fetchShopifyVariants = async () => {
      try {
        setVariantsLoading(true);
        const [variantsResponse, rfsnResponse, partnerIdResponse] =
          await Promise.all([
            fetch(
              `${CUSTOMER_API_URL}/products/shopify-variants?refersion_id=${encodeURIComponent(
                partnerKey
              )}`
            ),
            fetch(
              `${CUSTOMER_API_URL}/refersion/affiliate-rfsn?affiliate_id=${encodeURIComponent(
                partnerKey
              )}`
            ),
            fetch(
              `${CUSTOMER_API_URL}/refersion/partner-id?refersion_id=${encodeURIComponent(
                partnerKey
              )}`
            )
          ]);

        if (!variantsResponse.ok) {
          throw new Error('Failed to fetch Shopify variants');
        }
        const data: ShopifyVariantInfo[] = await variantsResponse.json();

        // Convert array to Map for quick lookups by product tag
        const variantsMap = new Map<string, ShopifyVariantInfo>();
        data.forEach(item => {
          variantsMap.set(item.productTag, item);
        });
        setShopifyVariants(variantsMap);

        if (rfsnResponse.ok) {
          const { rfsn } = (await rfsnResponse.json()) as { rfsn: string };
          setAffiliateRfsn(rfsn);
        }

        if (partnerIdResponse.ok) {
          const { partner_id } = (await partnerIdResponse.json()) as {
            partner_id: string;
          };
          setPartnerId(partner_id);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setVariantsLoading(false);
      }
    };

    fetchShopifyVariants();
  }, [partnerKey]);

  const handlePartnerKeyValidated = (key: string) => {
    setRestoredDashboardState(null);
    setPartnerKey(key);
  };

  const handleBack = () => {
    clearDashboardStateFromCurrentUrl();
    setRestoredDashboardState(null);
    setPartnerKey(null);
    setAffiliateRfsn(undefined);
  };

  if (!partnerKey) {
    return <PartnerKeyForm onValidated={handlePartnerKeyValidated} />;
  }

  // Keep the loading spinner here until shopify variant data is ready,
  // so DashboardMain always mounts with a fully populated shopifyVariants map.
  if (variantsLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-[#DFEDED]"
        style={{ fontFamily: 'Poppins, sans-serif' }}
      >
        <div className="text-center">
          <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#014876] border-r-transparent" />
          <p className="text-lg font-semibold text-[#014876]">
            Loading product variants...
          </p>
        </div>
      </div>
    );
  }

  return (
    <DashboardMain
      partnerKey={partnerKey}
      partnerId={partnerId}
      affiliateRfsn={affiliateRfsn}
      onBack={handleBack}
      shopifyVariants={shopifyVariants}
      initialConfig={restoredDashboardState?.config}
      initialConfiguringProductTag={
        restoredDashboardState?.configuringProductTag
      }
    />
  );
}
